"use client";

import { useQuery } from "@apollo/client";
import Radar from "components/TechRadar/Radar";
import { Point } from "components/TechRadar/interfaces/point";
import { UserBadge } from "components/ui/v2/shared/bundle/profile/UserBadge";
import { Modal } from "components/ui/v2";
import { gqlSkill } from "gql";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import { IRole } from "types/Role.type";
import { HomeLoadingStateV2 } from "./HomeLoadingStateV2";

interface DetailRadarPoint {
  name: string;
  lastname: string;
  role: IRole;
}

interface UserByVendorAndQuadrant {
  user: DetailRadarPoint;
  description: string;
  ring: string;
  quadrant: string;
}

interface ITechRadarResponse {
  usersByVendoAndQuadrant: UserByVendorAndQuadrant[];
}

interface RadarSelection {
  description: string;
  quadrantName: string;
  ringName: string;
  users: Array<{ user: DetailRadarPoint }>;
}

export function DashboardOrganizationTechRadarPanelV2() {
  const { t } = useTranslation("common");
  const [quadrants] = useState<string[]>([
    t("Techniques"),
    t("Tools"),
    t("Languages & Frameworks"),
    t("Platforms")
  ]);
  const [rings] = useState<string[]>([
    "Junior",
    "Semi-Senior",
    "Senior",
    "Expert"
  ]);
  const [loading, setLoading] = useState<boolean>(true);
  const [techData, setTechData] = useState<UserByVendorAndQuadrant[]>([]);
  const [selection, setSelection] = useState<RadarSelection | null>(null);

  const processRings = (data: UserByVendorAndQuadrant[]) => {
    const newData = data.map((user) => ({
      ...user,
      ring: rings[Number(user.ring)]
    }));
    setTechData(newData);
    setLoading(false);
  };

  useQuery<ITechRadarResponse>(gqlSkill.queries.USERS_BY_VENDOR_AND_QUADRANT, {
    fetchPolicy: "network-only",
    variables: {
      usersByVendorAndQuadrantInput: {}
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setLoading(true);
      processRings(data.usersByVendoAndQuadrant);
    }
  });

  const onClickRadar = (point: Point) => {
    const { info } = point;
    if (!info[0]) return;

    setSelection({
      description: info[0].description?.toUpperCase() ?? "",
      quadrantName: info[0].quadrant?.toUpperCase() ?? "",
      ringName: info[0].ring?.toUpperCase() ?? "",
      users: info.map((entry) => ({
        user: (entry as unknown as { user: DetailRadarPoint }).user
      }))
    });
  };

  return (
    <>
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
          {t("Tech Radar")}
        </h2>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <HomeLoadingStateV2 />
            <p className="mt-4 text-sm text-slate-600 dark:text-zinc-400">
              {t("Cargando tech radar")}
            </p>
          </div>
        ) : (
          <Radar
            quadrants={quadrants}
            rings={rings}
            data={techData}
            width="100%"
            onClick={onClickRadar}
          />
        )}
      </section>

      <Modal
        open={Boolean(selection)}
        onClose={() => setSelection(null)}
        title={selection?.description}
        description={
          selection ? `${selection.quadrantName} - ${selection.ringName}` : undefined
        }
        size="md"
      >
        <div className="space-y-2">
          {selection?.users.map((entry) => (
            <div
              key={`${entry.user.name}${entry.user.lastname}`}
              className="rounded-lg bg-slate-100 p-3 dark:bg-zinc-800"
            >
              <UserBadge
                user={entry.user}
                size={45}
                previewProfile
                showRole
                showEmail
              />
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
